/* =========================================================
   Collections — galerie fluide dans les cartes produit
   ========================================================= */
(function () {
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const scrollIdleDelay = 180;
  const hoverDelay = 30;
  let primaryPhotoObserver;
  let lastPageScroll = -Infinity;
  const notePageScroll = () => { lastPageScroll = performance.now(); };
  const pageIsScrolling = () => performance.now() - lastPageScroll < scrollIdleDelay;

  window.addEventListener('scroll', notePageScroll, { passive: true });
  window.addEventListener('wheel', (event) => {
    if (event.deltaY) notePageScroll();
  }, { passive: true });

  class DrapeurCardGallery {
    constructor(element) {
      this.element = element;
      this.track = element.querySelector('[data-gallery-track]');
      this.slides = Array.from(element.querySelectorAll('[data-gallery-slide]'));
      this.dots = Array.from(element.querySelectorAll('[data-gallery-dot]'));
      this.status = element.querySelector('[data-gallery-status]');
      this.prevButton = element.querySelector('[data-gallery-prev]');
      this.nextButton = element.querySelector('[data-gallery-next]');
      this.card = element.closest('.grid-product__content');
      this.index = 0;
      this.pointerId = null;
      this.startX = 0;
      this.startY = 0;
      this.lastX = 0;
      this.lastY = 0;
      this.startTime = 0;
      this.direction = null;
      this.hoverTimer = null;
      this.resetTimer = null;
      this.wrapTimer = null;
      this.position = 1;
      this.manualNavigation = false;
      this.imagePromises = new WeakMap();
      this.readyImages = new WeakSet();
      this.navigationToken = 0;
      this.preloadStarted = false;

      if (!this.track || this.slides.length < 2) return;

      // Boundary copies let a swipe continue by one image in either direction.
      this.leadingSlide = this.createBoundarySlide(this.slides.length - 1);
      this.trailingSlide = this.createBoundarySlide(0);
      // Keep the first real slide as :first-child for the card's shared styles.
      this.leadingSlide.style.order = '-1';
      this.track.append(this.leadingSlide, this.trailingSlide);
      this.bindEvents();
      this.slides[0].querySelectorAll('img').forEach((image) => {
        // A responsive source may finish successfully after an earlier failure.
        image.addEventListener('load', () => this.revealWhenReady());
      });
      this.update(false);
      element.dataset.galleryReady = 'true';
    }

    bindEvents() {
      this.track.addEventListener('transitionend', (event) => {
        if (event.target === this.track && event.propertyName === 'transform') this.finishWrap();
      });

      this.prevButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.goTo(this.index - 1);
      });

      this.nextButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.goTo(this.index + 1);
      });

      this.element.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.element.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.element.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.element.addEventListener('pointercancel', () => this.cancelDrag());
      this.element.addEventListener('dragstart', (event) => event.preventDefault());

      if (this.card) {
        this.card.addEventListener('mouseenter', () => {
          clearTimeout(this.resetTimer);
          this.preloadNearby();
          this.scheduleHover();
        });

        this.card.addEventListener('mousemove', () => this.scheduleHover());

        this.card.addEventListener('mouseleave', () => {
          clearTimeout(this.hoverTimer);
          this.hoverTimer = null;
          if (!finePointerQuery.matches) return;
          this.resetAfterScroll();
        });
      }
    }

    scheduleHover() {
      if (!finePointerQuery.matches || this.manualNavigation || this.index !== 0 || this.hoverTimer !== null || pageIsScrolling()) return;
      const scrollPosition = window.scrollY;
      this.hoverTimer = setTimeout(() => {
        this.hoverTimer = null;
        if (!this.card.isConnected || !this.card.matches(':hover') || !finePointerQuery.matches) return;
        if (pageIsScrolling() || window.scrollY !== scrollPosition || this.manualNavigation || this.index !== 0) return;
        this.goTo(1, false);
      }, hoverDelay);
    }

    resetAfterScroll() {
      clearTimeout(this.resetTimer);
      // A late image decode must not restart a preview after leaving the card.
      this.navigationToken++;
      this.manualNavigation = false;
      if (!this.card.isConnected || this.card.matches(':hover') || this.index === 0) return;
      if (pageIsScrolling()) {
        this.resetTimer = setTimeout(() => this.resetAfterScroll(), scrollIdleDelay);
        return;
      }
      this.goTo(0, false);
    }

    createBoundarySlide(index) {
      const slide = document.createElement('div');
      slide.className = 'drapeur-card-gallery__slide';
      slide.setAttribute('aria-hidden', 'true');
      slide.inert = true;
      const image = this.slides[index].querySelector('img');
      if (image) slide.append(image.cloneNode(true));
      return slide;
    }

    preloadNearby() {
      this.preloadStarted = true;
      [0, 1, 2, -1].forEach((offset) => this.loadSlide(this.index + offset, true));
      if (this.index === 0 || this.index === this.slides.length - 1) {
        this.loadImages(this.leadingSlide, true);
        this.loadImages(this.trailingSlide, true);
      }
    }

    loadSlide(index, eager = this.preloadStarted) {
      return this.loadImages(this.slides[(index + this.slides.length) % this.slides.length], eager);
    }

    loadImages(slide, eager = this.preloadStarted) {
      if (!slide) return Promise.resolve(false);

      const promises = Array.from(slide.querySelectorAll('img')).map((image) => {
        if (eager) image.loading = 'eager';
        if (image.dataset.src) {
          if (image.dataset.srcset) image.srcset = image.dataset.srcset;
          image.src = image.dataset.src;
          image.removeAttribute('data-src');
          image.removeAttribute('data-srcset');
        }
        if (this.readyImages.has(image) && (!image.complete || image.naturalWidth === 0)) {
          this.readyImages.delete(image);
          this.imagePromises.delete(image);
        }
        if (this.imagePromises.has(image)) return this.imagePromises.get(image);

        // Cached images stay instant on browsers without decode().
        if (image.complete && image.naturalWidth > 0 && typeof image.decode !== 'function') {
          this.readyImages.add(image);
          return Promise.resolve(true);
        }
        const loaded = new Promise((resolve) => {
          if (image.complete) return resolve(image.naturalWidth > 0);
          const finish = () => {
            image.removeEventListener('load', finish);
            image.removeEventListener('error', finish);
            resolve(image.naturalWidth > 0);
          };
          image.addEventListener('load', finish);
          image.addEventListener('error', finish);
        });
        const ready = loaded.then(async (success) => {
          if (!success) return false;
          if (typeof image.decode === 'function') {
            try { await image.decode(); } catch { return false; }
          }
          this.readyImages.add(image);
          return true;
        });
        this.imagePromises.set(image, ready);
        ready.then((success) => {
          // Only cache successful loads; a transient failure must remain retryable.
          if (!success && this.imagePromises.get(image) === ready) {
            this.imagePromises.delete(image);
          }
        });
        return ready;
      });
      return Promise.all(promises).then((ready) => ready.length > 0 && ready.every(Boolean));
    }

    slideIsReady(slide) {
      const images = Array.from(slide.querySelectorAll('img'));
      return images.length > 0 && images.every((image) => this.readyImages.has(image)
        && image.complete && image.naturalWidth > 0);
    }

    revealWhenReady() {
      // The gallery must not cover the base photo until its visible image is decoded.
      const slide = this.slides[this.index];
      this.loadImages(slide).then((ready) => {
        if (ready && slide === this.slides[this.index] && this.element.isConnected) {
          this.element.dataset.galleryImageReady = 'true';
        }
      });
    }

    finishWrap() {
      if (this.position > 0 && this.position <= this.slides.length) return;
      clearTimeout(this.wrapTimer);
      this.wrapTimer = null;
      this.position = this.index + 1;
      this.element.classList.add('is-dragging');
      this.track.style.transform = `translate3d(${-this.position * 100}%, 0, 0)`;
      // Commit the identical real image without animating back across the track.
      void this.track.offsetWidth;
      this.element.classList.remove('is-dragging');
    }

    goTo(nextIndex, manual = true) {
      const token = ++this.navigationToken;
      this.finishWrap();
      this.preloadNearby();
      if (manual) {
        this.manualNavigation = true;
        clearTimeout(this.hoverTimer);
        this.hoverTimer = null;
      }
      const count = this.slides.length;
      const wrappedIndex = ((nextIndex % count) + count) % count;
      if (wrappedIndex === this.index) {
        this.update(true);
        return;
      }

      const position = nextIndex === -1 && this.index === 0 ? 0
        : nextIndex === count && this.index === count - 1 ? count + 1
        : wrappedIndex + 1;
      const physicalSlides = [this.leadingSlide, ...this.slides, this.trailingSlide];
      const path = physicalSlides.slice(Math.min(this.position, position), Math.max(this.position, position) + 1);
      // Include the real destination as well as its boundary copy before recentering.
      const needed = [...new Set([...path, this.slides[wrappedIndex]])];
      const promises = needed.map((slide) => this.loadImages(slide, true));
      const move = () => {
        if (token !== this.navigationToken || !this.element.isConnected) return;
        this.position = position;
        this.index = wrappedIndex;
        this.update(true);
        if (position === 0 || position === count + 1) {
          // Fallback for reduced motion or an interrupted CSS transition.
          this.wrapTimer = setTimeout(() => this.finishWrap(), 280);
        }
      };
      if (needed.every((slide) => this.slideIsReady(slide))) {
        move();
      } else {
        // Keep the current photo and indicator while the destination is loading.
        this.update(true);
        Promise.all(promises).then((ready) => {
          if (ready.every(Boolean)) move();
        });
      }
    }

    update(animate) {
      this.loadSlide(this.index);
      if (this.preloadStarted) this.preloadNearby();

      this.element.classList.toggle('is-dragging', !animate);
      this.track.style.transform = `translate3d(${-this.position * 100}%, 0, 0)`;

      if (!animate) {
        requestAnimationFrame(() => this.element.classList.remove('is-dragging'));
      }

      this.revealWhenReady();

      this.slides.forEach((slide, slideIndex) => {
        slide.setAttribute('aria-hidden', slideIndex === this.index ? 'false' : 'true');
      });

      let activeDotIndex = this.index;
      let hasHiddenBefore = false;
      let hasHiddenAfter = false;

      if (this.slides.length > this.dots.length) {
        if (this.index < 2) {
          activeDotIndex = this.index;
        } else if (this.index >= this.slides.length - 2) {
          activeDotIndex = this.dots.length - (this.slides.length - this.index);
        } else {
          activeDotIndex = 2;
        }

        hasHiddenBefore = this.index > 2;
        hasHiddenAfter = this.index < this.slides.length - 3;
      }

      this.dots.forEach((dot, dotIndex) => {
        const isFirstEdge = dotIndex === 0 && hasHiddenBefore;
        const isLastEdge = dotIndex === this.dots.length - 1 && hasHiddenAfter;
        dot.classList.toggle('is-active', dotIndex === activeDotIndex);
        dot.classList.toggle('is-edge', isFirstEdge || isLastEdge);
      });

      this.prevButton.disabled = false;
      this.nextButton.disabled = false;

      if (this.status) {
        this.status.textContent = this.status.dataset.statusTemplate.replace('__INDEX__', String(this.index + 1));
      }
    }

    onPointerDown(event) {
      if (!mobileQuery.matches || event.button !== 0 || event.target.closest('button')) return;

      this.finishWrap();
      this.navigationToken++;
      this.preloadNearby();
      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.lastX = event.clientX;
      this.lastY = event.clientY;
      this.startTime = performance.now();
      this.direction = null;
    }

    onPointerMove(event) {
      if (event.pointerId !== this.pointerId) return;

      const deltaX = event.clientX - this.startX;
      const deltaY = event.clientY - this.startY;
      const absoluteX = Math.abs(deltaX);
      const absoluteY = Math.abs(deltaY);
      this.lastX = event.clientX;
      this.lastY = event.clientY;

      if (!this.direction && absoluteX >= 4 && absoluteX >= absoluteY * .85) {
        this.direction = 'horizontal';
        this.element.setPointerCapture(event.pointerId);
      } else if (!this.direction && absoluteY > 14 && absoluteY > absoluteX * 1.4) {
        this.direction = 'vertical';
      }

      if (this.direction !== 'horizontal') return;
      if (event.cancelable) event.preventDefault();

      const nextIndex = (this.index + (deltaX < 0 ? 1 : -1) + this.slides.length) % this.slides.length;
      const adjacent = deltaX > 0 && this.index === 0 ? this.leadingSlide
        : deltaX < 0 && this.index === this.slides.length - 1 ? this.trailingSlide
        : this.slides[nextIndex];
      this.loadImages(adjacent, true);
      // A long drag cannot expose an unloaded image or go past the boundary copy.
      const offset = this.slideIsReady(adjacent) && this.slideIsReady(this.slides[nextIndex])
        ? Math.max(-this.element.clientWidth, Math.min(deltaX, this.element.clientWidth)) : 0;
      this.element.classList.add('is-dragging');
      this.track.style.transform = `translate3d(calc(${-this.position * 100}% + ${offset}px), 0, 0)`;
    }

    onPointerUp(event) {
      if (event.pointerId !== this.pointerId) return;

      const deltaX = event.clientX - this.startX;
      const deltaY = event.clientY - this.startY;
      const elapsed = Math.max(performance.now() - this.startTime, 1);
      const velocity = Math.abs(deltaX) / elapsed;
      const swipeThreshold = Math.min(28, Math.max(14, this.element.clientWidth * .08));
      const isHorizontalGesture = this.direction === 'horizontal'
        || (!this.direction && Math.abs(deltaX) >= 8 && Math.abs(deltaX) > Math.abs(deltaY) * .8);
      const isSwipe = isHorizontalGesture
        && (Math.abs(deltaX) >= swipeThreshold || (Math.abs(deltaX) >= 8 && velocity > .16));

      if (isSwipe) {
        this.goTo(this.index + (deltaX < 0 ? 1 : -1));
      } else if (isHorizontalGesture) {
        this.update(true);
      } else if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) {
        const productUrl = this.element.dataset.productUrl;
        if (productUrl) window.location.assign(productUrl);
      }

      this.releasePointer(event.pointerId);
    }

    cancelDrag() {
      if (this.pointerId === null) return;

      const deltaX = this.lastX - this.startX;
      const swipeThreshold = Math.min(28, Math.max(14, this.element.clientWidth * .08));
      if (this.direction === 'horizontal' && Math.abs(deltaX) >= swipeThreshold) {
        this.goTo(this.index + (deltaX < 0 ? 1 : -1));
      } else {
        this.update(true);
      }
      this.releasePointer(this.pointerId);
    }

    releasePointer(pointerId) {
      if (this.element.hasPointerCapture(pointerId)) {
        this.element.releasePointerCapture(pointerId);
      }
      this.pointerId = null;
      this.direction = null;
      this.element.classList.remove('is-dragging');
    }
  }

  function initCardGalleries(root = document) {
    root.querySelectorAll('[data-drapeur-card-gallery]:not([data-gallery-ready])')
      .forEach((gallery) => new DrapeurCardGallery(gallery));
    preloadCardPhotos(root);
  }

  function preloadCardPhotos(root) {
    if (typeof IntersectionObserver !== 'function') return;
    if (!primaryPhotoObserver) {
      // Give primary photos two viewports of lead time without fetching galleries.
      const margin = Math.max(1200, (window.innerHeight || 600) * 2);
      primaryPhotoObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.querySelectorAll(':scope > .image-wrap img, :scope > .grid__image-ratio img, [data-gallery-slide]:first-child img')
            .forEach((image) => { image.loading = 'eager'; });
          observer.unobserve(entry.target);
        });
      }, { rootMargin: `${margin}px 0px` });
    }
    root.querySelectorAll(':is(.template-collection, .template-search, .drapeur-listing-cards) .grid-product__image-mask:not([data-primary-preload-ready])')
      .forEach((mask) => {
        mask.dataset.primaryPreloadReady = 'true';
        primaryPhotoObserver.observe(mask);
      });
  }

  document.addEventListener('DOMContentLoaded', () => initCardGalleries(document));
  document.addEventListener('collection:reloaded', () => initCardGalleries(document));
  document.addEventListener('recommendations:loaded', (event) => initCardGalleries(event.detail?.section || document));
  document.addEventListener('shopify:section:load', (event) => initCardGalleries(event.target));
})();

/* =========================================================
   Panier mobile — verrouillage fiable du scroll de la page
   ========================================================= */
(function () {
  const mobileQuery = window.matchMedia('(max-width: 768px)');
  const root = document.documentElement;
  let cartDrawer;
  let drawerObserver;
  let locked = false;
  let pageScrollY = 0;
  let previousOffset = '';

  function lockPageScroll() {
    if (locked || !mobileQuery.matches) return;

    pageScrollY = window.scrollY || window.pageYOffset || 0;
    previousOffset = root.style.getPropertyValue('--drapeur-cart-scroll-offset');
    root.style.setProperty('--drapeur-cart-scroll-offset', `${-pageScrollY}px`);
    root.classList.add('drapeur-cart-scroll-locked');
    locked = true;
  }

  function unlockPageScroll() {
    if (!locked) return;

    root.classList.remove('drapeur-cart-scroll-locked');
    if (previousOffset) {
      root.style.setProperty('--drapeur-cart-scroll-offset', previousOffset);
    } else {
      root.style.removeProperty('--drapeur-cart-scroll-offset');
    }

    const restoreScrollY = pageScrollY;
    locked = false;
    window.scrollTo(0, restoreScrollY);
  }

  function syncPageScrollLock() {
    const cartIsOpen = cartDrawer
      && cartDrawer.classList.contains('drawer--is-open')
      && root.classList.contains('js-drawer-open');

    if (cartIsOpen && mobileQuery.matches) {
      lockPageScroll();
    } else {
      unlockPageScroll();
    }
  }

  function initCartScrollLock() {
    cartDrawer = document.getElementById('CartDrawer');
    if (!cartDrawer || drawerObserver) return;

    drawerObserver = new MutationObserver(syncPageScrollLock);
    drawerObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['class']
    });

    document.addEventListener('drawerOpen.CartDrawer', lockPageScroll);

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', syncPageScrollLock);
    } else {
      mobileQuery.addListener(syncPageScrollLock);
    }

    window.addEventListener('pageshow', syncPageScrollLock);
    window.addEventListener('pagehide', unlockPageScroll);
    syncPageScrollLock();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartScrollLock, { once: true });
  } else {
    initCartScrollLock();
  }
})();

(() => {
  const drawerIds = {
    '5511bb6f-685a-4aa2-a522-b5b5e763a590': 'details',
    '535e1fde-eeff-415f-9799-8eae7cf6039a': 'shipping'
  };
  const normalize = (text) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const trimLabel = (text) => text.replace(/\s*[:：]\s*$/, '').trim();

  function fieldKind(label) {
    const text = normalize(label);
    if (/composition|material|composizione|composicion|composicao|zusammensetzung/.test(text)) return 'composition';
    if (/fabrication|manufactur|made in|origin|herstellung|produzione|fabricacion|fabricacao/.test(text)) return 'origin';
    if (/reference|referencia|riferimento|artikelnummer|sku/.test(text)) return 'reference';
    return 'full';
  }

  function formatDetails(content) {
    content.querySelectorAll('ul').forEach((list) => {
      const items = Array.from(list.children);
      const fields = items.map((item) => {
        const wrapper = item.children.length === 1 && item.firstElementChild.tagName === 'P' ? item.firstElementChild : item;
        const label = wrapper.firstElementChild;
        if (!label || !['STRONG', 'B'].includes(label.tagName)) return null;
        const preceding = Array.from(wrapper.childNodes).slice(0, Array.from(wrapper.childNodes).indexOf(label));
        if (preceding.some((node) => node.textContent.trim())) return null;
        return { wrapper, label, kind: fieldKind(label.textContent) };
      });
      if (!fields.length || fields.some((field) => !field) || list.querySelector('ul, ol')) return;
      const grid = document.createElement('dl');
      grid.className = 'drapeur-drawer-grid';
      const order = { composition: 0, origin: 1, full: 2, reference: 3 };
      fields.sort((a, b) => order[a.kind] - order[b.kind]).forEach(({ wrapper, label, kind }) => {
        const field = document.createElement('div');
        field.className = `drapeur-drawer-field drapeur-drawer-field--${kind}`;
        const term = document.createElement('dt');
        term.className = 'drapeur-drawer-label';
        term.textContent = trimLabel(label.textContent);
        label.remove();
        const value = document.createElement('dd');
        value.className = 'drapeur-drawer-value';
        while (wrapper.firstChild) value.append(wrapper.firstChild);
        const firstText = Array.from(value.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
        if (firstText && !Array.from(value.childNodes).slice(0, Array.from(value.childNodes).indexOf(firstText)).some((node) => node.textContent.trim())) {
          firstText.textContent = firstText.textContent.replace(/^\s*[:：]?\s*/, '');
        }
        field.append(term, value);
        grid.append(field);
      });
      list.replaceWith(grid);
    });
  }

  function formatShipping(content) {
    content.querySelectorAll('p').forEach((paragraph) => {
      const list = paragraph.nextElementSibling;
      const bold = paragraph.querySelector('strong, b');
      if (!list || list.tagName !== 'UL' || !bold || paragraph.textContent.trim() !== bold.textContent.trim()) return;
      const group = document.createElement('div');
      group.className = 'drapeur-drawer-service';
      paragraph.before(group);
      paragraph.classList.add('drapeur-drawer-label');
      paragraph.textContent = trimLabel(paragraph.textContent);
      list.setAttribute('role', 'list');
      group.append(paragraph, list);
    });
    const link = content.querySelector('a[href*="/pages/expedition-et-livraison"]');
    const worldwide = link && link.closest('p');
    if (worldwide && !worldwide.closest('.drapeur-drawer-service')) {
      worldwide.classList.add('drapeur-drawer-world');
      worldwide.parentElement.append(worldwide);
    }
  }

  function initDrawers(root = document) {
    root.querySelectorAll('.product-section--main .product-block--tab:not([data-drapeur-drawer])').forEach((block) => {
      const panel = block.querySelector('.collapsible-content[id]');
      const id = panel && Object.keys(drawerIds).find((key) => panel.id.startsWith(`Product-content-${key}`));
      const content = panel && panel.querySelector('.collapsible-content__inner');
      if (!id || !content) return;
      block.dataset.drapeurDrawer = drawerIds[id];
      if (drawerIds[id] === 'details') formatDetails(content);
      else formatShipping(content);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initDrawers());
  else initDrawers();
  document.addEventListener('shopify:section:load', (event) => initDrawers(event.target));
})();



if (!customElements.get('drapeur-installments')) {
  customElements.define('drapeur-installments', class extends HTMLElement {
    connectedCallback() {
      this.updateAmount(Number(this.dataset.amount));
      if (this.dataset.context === 'product') {
        this.scope = this.closest('[data-section-type="product"]');
        this.onVariantChange = (event) => {
          this.updateAmount(event.detail.variant ? event.detail.variant.price : 0);
        };
        this.scope?.addEventListener('variantChange', this.onVariantChange, true);
      } else {
        const products = this.closest('form')?.querySelector('[data-products]');
        if (products) {
          this.observer = new MutationObserver(() => {
            const items = products.querySelector('[data-cart-subtotal]');
            if (items) this.updateAmount(Number(items.dataset.cartSubtotal));
          });
          this.observer.observe(products, { childList: true });
        }
      }
    }

    disconnectedCallback() {
      this.scope?.removeEventListener('variantChange', this.onVariantChange, true);
      this.observer?.disconnect();
    }

    updateAmount(amount) {
      this.hidden = this.dataset.eligible !== 'true' || !Number.isFinite(amount) || amount < 10000 || amount > 500000;
      if (this.hidden) {
        this.querySelector('drapeur-payment-info')?.close?.();
        return;
      }
      const formatted = new Intl.NumberFormat('fr-FR', {
        style: 'currency', currency: 'EUR'
      }).format(Math.floor(amount / 4) / 100);
      this.querySelector('[data-installment-amount]').textContent = formatted;
    }
  });
}

if (!customElements.get('drapeur-payment-info')) {
  let paymentInfoId = 0;
  customElements.define('drapeur-payment-info', class extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('button');
      this.tip = this.querySelector('[popover]');
      this.tip.id ||= `drapeur-payment-info-${++paymentInfoId}`;
      this.trigger.setAttribute('aria-describedby', this.tip.id);
      this.trigger.setAttribute('aria-controls', this.tip.id);
      this.events = new AbortController();
      const on = (el, name, fn, options = {}) => el.addEventListener(name, fn, { ...options, signal: this.events.signal });
      on(this.trigger, 'pointerenter', (event) => { if (event.pointerType === 'mouse') this.open(); });
      on(this.trigger, 'pointerleave', () => this.delayClose());
      on(this.tip, 'pointerenter', () => clearTimeout(this.closeTimer));
      on(this.tip, 'pointerleave', () => this.delayClose());
      on(this.trigger, 'focus', () => { if (this.trigger.matches(':focus-visible')) this.open(); });
      on(this.trigger, 'blur', () => this.close());
      on(this.trigger, 'click', () => this.tip.matches(':popover-open') ? this.close() : this.open());
      on(document, 'pointerdown', (event) => { if (!this.contains(event.target)) this.close(); });
      on(document, 'keydown', (event) => { if (event.key === 'Escape') this.close(); });
      on(window, 'resize', () => this.close());
      on(document, 'scroll', () => this.close(), { capture: true, passive: true });
    }
    open() {
      clearTimeout(this.closeTimer);
      this.tip.showPopover();
      const anchor = this.trigger.getBoundingClientRect();
      const box = this.tip.getBoundingClientRect();
      const left = Math.max(12, Math.min(anchor.right - box.width, window.innerWidth - box.width - 12));
      const below = anchor.bottom + 8;
      const top = below + box.height <= window.innerHeight - 12 ? below : Math.max(12, anchor.top - box.height - 8);
      this.tip.style.left = `${left}px`;
      this.tip.style.top = `${top}px`;
      this.trigger.setAttribute('aria-expanded', 'true');
    }
    delayClose() {
      this.closeTimer = setTimeout(() => this.close(), 150);
    }
    close() {
      clearTimeout(this.closeTimer);
      if (!this.tip || !this.trigger) return;
      if (this.tip.matches(':popover-open')) this.tip.hidePopover();
      this.trigger.setAttribute('aria-expanded', 'false');
    }
    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      this.events?.abort();
      if (this.tip?.matches(':popover-open')) this.tip.hidePopover();
    }
  });
}
